// src/pages/WHSCoverageMapping.tsx — WHS Coverage Mapping
// Job Activity Register → SWMS coverage status per active job.
// Shows WHSO which planned high-risk activities have an approved SWMS
// and which are uncovered before work commences.
// WHS Module Revision v2.0 — Section 11.4 Components 1 & 2.
// Reads: MOCK_SWMS (approved), MOCK_JOBS (active)
// Writes: coverage assignments (local state → POST /api/job-activities/:id/coverage)

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_SWMS } from '../utils/mockDataTier3'
import { MOCK_JOBS } from '../utils/mockData'
import { classifyReg291, runA04GapDetection, addAgentActivity } from '../utils/mockDataProjectGaps'
import {
  ShieldCheck, ShieldX, ShieldAlert, Shield,
  CheckCircle, AlertTriangle, Clock, X, ChevronDown, ChevronRight,
  Plus, Link2, Users, Search, RefreshCw, Download, Eye, Zap, Bot,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

type CoverageStatus = 'no_swms' | 'swms_unsigned' | 'covered' | 'not_required'
type ActivitySource  = 'work_instruction' | 'drawing_ifc' | 'itp_hold_point' | 'manual'

interface JobActivity {
  id:                    string
  jobId:                 string
  jobNumber:             string
  activityDescription:   string
  location:              string
  reg291Categories:      string[]
  source:                ActivitySource
  sourceRef:             string
  plannedStartDate:      string
  coverageStatus:        CoverageStatus
  linkedSwmsId:          string | null
  linkedSwmsNumber:      string | null
  linkedSwmsTitle:       string | null
  assignedCrewCount:     number
  signedOnCount:         number
  jhaRequired:           boolean
}

// ─── Mock Job Activity Register ───────────────────────────────────────────────
// In production this reads from /api/job-activities.
// Replaced by live Neon data once the Job Activity Register table is migrated.

const MOCK_ACTIVITIES: JobActivity[] = [
  // ── j1 · TP-2026-001 · Acacia Ridge ITS Upgrade ─────────────────────────
  {
    id: 'ja001', jobId: 'j1', jobNumber: 'TP-2026-001',
    activityDescription: 'Traffic signal head installation — pole mounting and head alignment',
    location: 'All intersections, Phase A',
    reg291Categories: ['Adjacent to road in use by traffic'],
    source: 'work_instruction', sourceRef: 'WI-TP001-003',
    plannedStartDate: '2026-08-11',
    coverageStatus: 'covered',
    linkedSwmsId: 'swms1', linkedSwmsNumber: 'SWMS-2026-001',
    linkedSwmsTitle: 'Traffic Signal Installation and Electrical Works',
    assignedCrewCount: 3, signedOnCount: 3, jhaRequired: false,
  },
  {
    id: 'ja002', jobId: 'j1', jobNumber: 'TP-2026-001',
    activityDescription: 'Conduit installation and pit excavation — pavement works',
    location: 'All pit locations, Kessels Rd corridor',
    reg291Categories: ['Adjacent to road in use by traffic', 'Trench or shaft excavation deeper than 1.5 m'],
    source: 'drawing_ifc', sourceRef: 'DWG-TP001-C02 Rev B (IFC)',
    plannedStartDate: '2026-08-07',
    coverageStatus: 'covered',
    linkedSwmsId: 'swms1', linkedSwmsNumber: 'SWMS-2026-001',
    linkedSwmsTitle: 'Traffic Signal Installation and Electrical Works',
    assignedCrewCount: 3, signedOnCount: 3, jhaRequired: false,
  },
  {
    id: 'ja003', jobId: 'j1', jobNumber: 'TP-2026-001',
    activityDescription: 'Underground cable pulling — 6mm power cable in conduit',
    location: 'Full cable route, pits 1–12',
    reg291Categories: ['Energised electrical installation or services'],
    source: 'work_instruction', sourceRef: 'WI-TP001-005',
    plannedStartDate: '2026-08-14',
    coverageStatus: 'covered',
    linkedSwmsId: 'swms1', linkedSwmsNumber: 'SWMS-2026-001',
    linkedSwmsTitle: 'Traffic Signal Installation and Electrical Works',
    assignedCrewCount: 2, signedOnCount: 2, jhaRequired: false,
  },
  {
    id: 'ja004', jobId: 'j1', jobNumber: 'TP-2026-001',
    activityDescription: 'SCATS controller installation and termination — Energex MSB connections',
    location: 'SCATS controller cabinet, Kessels Rd NW corner',
    reg291Categories: ['Energised electrical installation or services', 'Energised electrical — high voltage'],
    source: 'itp_hold_point', sourceRef: 'ITP-TP001-001 Item 3.1',
    plannedStartDate: '2026-08-25',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 2, signedOnCount: 0, jhaRequired: true,
  },
  {
    id: 'ja005', jobId: 'j1', jobNumber: 'TP-2026-001',
    activityDescription: 'EWP operation — signal head adjustment at height',
    location: 'Poles 7, 9, 11 (pending ITP sign-off)',
    reg291Categories: ['Working at heights with risk of fall > 2 m'],
    source: 'manual', sourceRef: 'Added by WHSO 4 Aug',
    plannedStartDate: '2026-08-19',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 1, signedOnCount: 0, jhaRequired: true,
  },

  // ── j4 · TP-2026-004 · Bruce Highway Smart Motorway ──────────────────────
  {
    id: 'ja006', jobId: 'j4', jobNumber: 'TP-2026-004',
    activityDescription: 'HV cable installation — 11kV 3C underground cable jointing and termination',
    location: 'Pits A–F, Annerley substation approach',
    reg291Categories: ['Energised electrical — high voltage', 'Energised electrical installation or services'],
    source: 'work_instruction', sourceRef: 'WI-TP004-001',
    plannedStartDate: '2026-08-07',
    coverageStatus: 'covered',
    linkedSwmsId: 'swms2', linkedSwmsNumber: 'SWMS-2026-004',
    linkedSwmsTitle: 'HV Cable Replacement — 11kV Underground Works',
    assignedCrewCount: 1, signedOnCount: 1, jhaRequired: false,
  },
  {
    id: 'ja007', jobId: 'j4', jobNumber: 'TP-2026-004',
    activityDescription: 'Confined space entry — 11kV cable vault and large-format cable pits',
    location: 'Cable vault, Annerley sub main entry',
    reg291Categories: ['Confined space'],
    source: 'drawing_ifc', sourceRef: 'DWG-TP004-E03 Rev A (IFC)',
    plannedStartDate: '2026-08-09',
    coverageStatus: 'covered',
    linkedSwmsId: 'swms2', linkedSwmsNumber: 'SWMS-2026-004',
    linkedSwmsTitle: 'HV Cable Replacement — 11kV Underground Works',
    assignedCrewCount: 2, signedOnCount: 2, jhaRequired: false,
  },
  {
    id: 'ja008', jobId: 'j4', jobNumber: 'TP-2026-004',
    activityDescription: 'Mechanical excavation near live HV and LV services — trench >1.5m depth',
    location: 'Trench section B–D, 1.9m depth',
    reg291Categories: ['Trench or shaft excavation deeper than 1.5 m', 'Adjacent to road in use by traffic'],
    source: 'work_instruction', sourceRef: 'WI-TP004-002',
    plannedStartDate: '2026-08-11',
    coverageStatus: 'swms_unsigned',
    linkedSwmsId: 'swms2', linkedSwmsNumber: 'SWMS-2026-004',
    linkedSwmsTitle: 'HV Cable Replacement — 11kV Underground Works',
    assignedCrewCount: 3, signedOnCount: 1, jhaRequired: false,
  },

  // ── j3 · TP-2026-003 · Gold Coast Light Rail Stage 4 ─────────────────────
  // SWMS-2026-007 is UNDER_REVIEW — not yet approved
  {
    id: 'ja009', jobId: 'j3', jobNumber: 'TP-2026-003',
    activityDescription: 'Pedestrian signal installation and electrical termination',
    location: 'GC Highway at Bundall Rd — all four corners',
    reg291Categories: ['Energised electrical installation or services', 'Adjacent to road in use by traffic'],
    source: 'work_instruction', sourceRef: 'WI-TP003-001',
    plannedStartDate: '2026-08-12',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 2, signedOnCount: 0, jhaRequired: true,
  },
  {
    id: 'ja010', jobId: 'j3', jobNumber: 'TP-2026-003',
    activityDescription: 'Traffic management — TC operations on Gold Coast Highway (60km/h zone)',
    location: 'GC Highway northbound and southbound approaches',
    reg291Categories: ['Adjacent to road in use by traffic'],
    source: 'drawing_ifc', sourceRef: 'DWG-TP003-TM01 Rev A (IFC)',
    plannedStartDate: '2026-08-12',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 2, signedOnCount: 0, jhaRequired: false,
  },
  {
    id: 'ja011', jobId: 'j3', jobNumber: 'TP-2026-003',
    activityDescription: 'Conduit installation — shallow trench in footpath (0.45m depth)',
    location: 'All four footpath areas adjacent to intersection',
    reg291Categories: [],
    source: 'work_instruction', sourceRef: 'WI-TP003-002',
    plannedStartDate: '2026-08-13',
    coverageStatus: 'not_required',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 1, signedOnCount: 0, jhaRequired: false,
  },

  // ── j2 · TP-2026-002 · Ipswich Motorway Signals ──────────────────────────
  {
    id: 'ja012', jobId: 'j2', jobNumber: 'TP-2026-002',
    activityDescription: 'Ramp metering signal installation on motorway gantry — 9m height',
    location: 'Pacific Motorway northbound on-ramp — Underwood',
    reg291Categories: ['Working at heights with risk of fall > 2 m', 'Adjacent to road in use by traffic'],
    source: 'drawing_ifc', sourceRef: 'DWG-TP002-S01 Rev C (IFC)',
    plannedStartDate: '2026-08-18',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 3, signedOnCount: 0, jhaRequired: true,
  },
  {
    id: 'ja013', jobId: 'j2', jobNumber: 'TP-2026-002',
    activityDescription: 'EWP operation — motorway gantry access and electrical connections',
    location: 'All gantry structures, Pacific Motorway',
    reg291Categories: ['Working at heights with risk of fall > 2 m', 'Movement of powered mobile plant'],
    source: 'work_instruction', sourceRef: 'WI-TP002-003',
    plannedStartDate: '2026-08-18',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 2, signedOnCount: 0, jhaRequired: true,
  },
  {
    id: 'ja014', jobId: 'j2', jobNumber: 'TP-2026-002',
    activityDescription: 'Motorway shoulder works — traffic management at 100km/h',
    location: 'Pacific Motorway northbound shoulder, 1.8km section',
    reg291Categories: ['Adjacent to road in use by traffic'],
    source: 'manual', sourceRef: 'Added by PM 2 Aug',
    plannedStartDate: '2026-08-18',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 2, signedOnCount: 0, jhaRequired: false,
  },

  // ── j5 · TP-2026-005 · Gympie Road Corridor ──────────────────────────────
  {
    id: 'ja015', jobId: 'j5', jobNumber: 'TP-2026-005',
    activityDescription: 'ITS cabinet electrical installation — data comms and power',
    location: 'All 6 cabinet locations, Gympie Rd northbound',
    reg291Categories: ['Energised electrical installation or services'],
    source: 'manual', sourceRef: 'Added by WHSO 5 Aug',
    plannedStartDate: '2026-08-21',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 2, signedOnCount: 0, jhaRequired: false,
  },
  {
    id: 'ja016', jobId: 'j5', jobNumber: 'TP-2026-005',
    activityDescription: 'Fibre optic conduit — pavement boring adjacent to live road',
    location: 'Gympie Rd between Stafford Rd and Kedron Brook Rd',
    reg291Categories: ['Adjacent to road in use by traffic', 'Trench or shaft excavation deeper than 1.5 m'],
    source: 'drawing_ifc', sourceRef: 'DWG-TP005-C01 Rev A (IFC)',
    plannedStartDate: '2026-08-22',
    coverageStatus: 'no_swms',
    linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null,
    assignedCrewCount: 3, signedOnCount: 0, jhaRequired: false,
  },
]

// ─── Approved SWMS by job (for assignment modal) ──────────────────────────────

function getApprovedSwmsForJob(jobId: string) {
  return MOCK_SWMS.filter(s => s.jobId === jobId && s.status === 'APPROVED')
}

// ─── Coverage config ──────────────────────────────────────────────────────────

const COVERAGE_CONFIG: Record<CoverageStatus, {
  label: string; short: string;
  bg: string; text: string; border: string; dot: string;
  rowBg: string;
  Icon: any;
}> = {
  covered: {
    label: 'Covered — SWMS approved & crew signed on',
    short: 'Covered',
    bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300',
    dot: 'bg-green-500', rowBg: 'bg-green-50/40',
    Icon: ShieldCheck,
  },
  swms_unsigned: {
    label: 'SWMS approved — crew sign-on incomplete',
    short: 'Crew unsigned',
    bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300',
    dot: 'bg-amber-500', rowBg: 'bg-amber-50/40',
    Icon: Clock,
  },
  no_swms: {
    label: 'No approved SWMS — work must not commence',
    short: 'No SWMS',
    bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300',
    dot: 'bg-red-500', rowBg: 'bg-red-50/30',
    Icon: ShieldX,
  },
  not_required: {
    label: 'WHSO assessed — no HRCW applies',
    short: 'Not required',
    bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300',
    dot: 'bg-gray-400', rowBg: '',
    Icon: CheckCircle,
  },
}

const SOURCE_LABELS: Record<ActivitySource, string> = {
  work_instruction: 'Work Instruction',
  drawing_ifc:      'Drawing (IFC)',
  itp_hold_point:   'ITP Hold Point',
  manual:           'Manual entry',
}

// ─── Assign SWMS modal ────────────────────────────────────────────────────────

function AssignSWMSModal({
  activity,
  onClose,
  onAssign,
}: {
  activity: JobActivity
  onClose: () => void
  onAssign: (activityId: string, swmsId: string) => void
}) {
  const approvedSwms = getApprovedSwmsForJob(activity.jobId)
  const [selected, setSelected] = useState('')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">Assign SWMS to activity</h3>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{activity.activityDescription}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {approvedSwms.length === 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>No approved SWMS for {activity.jobNumber}.</strong>
                  <p className="mt-1 text-xs">An approved SWMS must exist before it can be assigned to this activity. Go to the SWMS module to create and approve one first.</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
                <strong>After assigning:</strong> The coverage status will update to <em>Crew unsigned</em>. All assigned crew must sign on to the SWMS before work commences to reach <em>Covered</em> status.
              </div>

              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Select the approved SWMS that covers this activity:</p>
                <div className="space-y-2">
                  {approvedSwms.map(s => (
                    <label key={s.id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      selected === s.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input type="radio" name="swms" value={s.id} checked={selected === s.id}
                        onChange={() => setSelected(s.id)} className="mt-0.5 accent-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono font-bold text-gray-700">{s.swmsNumber}</span>
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Rev {s.revision} · Approved</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{s.title}</p>
                        {s.highRiskActivities?.length > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Covers: {(s.highRiskActivities as string[]).slice(0, 2).join(' · ')}{s.highRiskActivities.length > 2 ? ` +${s.highRiskActivities.length - 2} more` : ''}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={() => { onAssign(activity.id, selected); onClose() }}
                  disabled={!selected}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
                >
                  <Link2 className="w-4 h-4" />
                  Assign SWMS
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Mark not required modal ──────────────────────────────────────────────────

function MarkNotRequiredModal({
  activity,
  onClose,
  onConfirm,
}: {
  activity: JobActivity
  onClose: () => void
  onConfirm: (activityId: string, reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Confirm — SWMS not required</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
            As WHSO, you are confirming that this activity does not constitute High-Risk Construction Work under Schedule 2 of the WHS Regulation 2011 (Qld) and therefore does not require a SWMS. This decision is recorded with your name and timestamp.
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason for assessment *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)}
              className="input w-full resize-none h-20 text-sm"
              placeholder="e.g. Activity involves less than 1.5m excavation depth in stable ground — HRCW category does not apply." />
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={() => { onConfirm(activity.id, reason); onClose() }} disabled={!reason.trim()}
              className="flex-1 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium disabled:opacity-40">
              Confirm — not required
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Activity row ─────────────────────────────────────────────────────────────

function ActivityRow({
  activity,
  onAssign,
  onMarkNotRequired,
  isWHSO,
}: {
  activity: JobActivity
  onAssign: (a: JobActivity) => void
  onMarkNotRequired: (a: JobActivity) => void
  isWHSO: boolean
}) {
  const cfg    = COVERAGE_CONFIG[activity.coverageStatus]
  const CfgIcon = cfg.Icon
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr className={`border-b border-gray-100 hover:bg-gray-50/60 transition-colors ${cfg.rowBg}`}>
        {/* Status */}
        <td className="px-4 py-3 w-36">
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
            <CfgIcon className="w-3 h-3 flex-shrink-0" />
            {cfg.short}
          </div>
        </td>

        {/* Activity */}
        <td className="px-3 py-3">
          <div className="text-sm font-medium text-gray-900 leading-snug">{activity.activityDescription}</div>
          <div className="text-xs text-gray-400 mt-0.5">{activity.location}</div>
        </td>

        {/* Reg 291 */}
        <td className="px-3 py-3 w-40">
          {activity.reg291Categories.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {activity.reg291Categories.slice(0, 2).map(c => (
                <span key={c} className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded leading-tight">
                  {c.length > 28 ? c.slice(0, 28) + '…' : c}
                </span>
              ))}
              {activity.reg291Categories.length > 2 && (
                <span className="text-[10px] text-gray-400">+{activity.reg291Categories.length - 2}</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">None assessed</span>
          )}
        </td>

        {/* Source */}
        <td className="px-3 py-3 w-32">
          <div className="text-xs text-gray-600">{SOURCE_LABELS[activity.source]}</div>
          <div className="text-[10px] text-gray-400 font-mono mt-0.5">{activity.sourceRef}</div>
        </td>

        {/* Planned date */}
        <td className="px-3 py-3 w-24 text-xs text-gray-600 tabular-nums whitespace-nowrap">
          {new Date(activity.plannedStartDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
        </td>

        {/* SWMS */}
        <td className="px-3 py-3 w-40">
          {activity.linkedSwmsId ? (
            <div>
              <div className="text-xs font-mono font-semibold text-blue-700">{activity.linkedSwmsNumber}</div>
              <div className="text-[10px] text-gray-500 leading-snug mt-0.5 line-clamp-1">{activity.linkedSwmsTitle}</div>
              {activity.coverageStatus === 'swms_unsigned' && (
                <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
                  <Users className="w-2.5 h-2.5" />
                  {activity.signedOnCount}/{activity.assignedCrewCount} signed
                </div>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">None assigned</span>
          )}
        </td>

        {/* Actions */}
        <td className="px-3 py-3 w-36">
          <div className="flex items-center gap-1.5 flex-wrap">
            {activity.coverageStatus === 'no_swms' && isWHSO && (
              <>
                <button onClick={() => onAssign(activity)}
                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1">
                  <Link2 className="w-3 h-3" />Assign
                </button>
                <button onClick={() => onMarkNotRequired(activity)}
                  className="text-xs border border-gray-300 text-gray-500 hover:bg-gray-50 px-2 py-1.5 rounded-lg transition-colors">
                  N/A
                </button>
              </>
            )}
            {activity.coverageStatus === 'swms_unsigned' && (
              <>
                <button onClick={() => setExpanded(!expanded)}
                  className="text-xs border border-amber-300 text-amber-700 hover:bg-amber-50 px-2 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                  <Users className="w-3 h-3" />Sign-on
                </button>
              </>
            )}
            {activity.coverageStatus === 'covered' && (
              <span className="text-xs text-green-700 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />Ready
              </span>
            )}
            {activity.coverageStatus === 'not_required' && (
              <span className="text-xs text-gray-400 italic">Assessed</span>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded sign-on row */}
      {expanded && activity.coverageStatus === 'swms_unsigned' && (
        <tr>
          <td colSpan={7} className="px-4 pb-3 bg-amber-50/60">
            <div className="border border-amber-200 rounded-xl p-3 text-xs text-amber-800 bg-white">
              <strong>{activity.assignedCrewCount - activity.signedOnCount} crew member{activity.assignedCrewCount - activity.signedOnCount !== 1 ? 's' : ''} have not yet signed on to {activity.linkedSwmsNumber}.</strong>
              <p className="mt-1 text-amber-700">In full production, this panel will list each crew member with their sign-on status and a link to send a mobile sign-on request. Digital sign-on is captured in the SWMS module → Worker sign-on tab.</p>
              <button onClick={() => setExpanded(false)} className="mt-2 text-xs text-amber-600 underline">Close</button>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ─── Per-job panel ────────────────────────────────────────────────────────────

function JobCoveragePanel({
  jobId,
  activities,
  isWHSO,
  onAssign,
  onMarkNotRequired,
}: {
  jobId: string
  activities: JobActivity[]
  isWHSO: boolean
  onAssign: (a: JobActivity) => void
  onMarkNotRequired: (a: JobActivity) => void
}) {
  const [open, setOpen] = useState(true)
  const job = MOCK_JOBS.find(j => j.id === jobId)
  if (!job) return null

  const covered       = activities.filter(a => a.coverageStatus === 'covered').length
  const unsigned      = activities.filter(a => a.coverageStatus === 'swms_unsigned').length
  const uncovered     = activities.filter(a => a.coverageStatus === 'no_swms').length
  const notRequired   = activities.filter(a => a.coverageStatus === 'not_required').length
  const relevant      = activities.length - notRequired   // activities that need SWMS
  const coveredPct    = relevant > 0 ? Math.round(((covered + unsigned) / relevant) * 100) : 100

  const barColor = uncovered > 0 ? 'bg-red-500' : unsigned > 0 ? 'bg-amber-500' : 'bg-green-500'
  const headerBg = uncovered > 0 ? 'bg-red-50 border-red-200' : unsigned > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Job header */}
      <button
        onClick={() => setOpen(!open)}
        className={`w-full text-left px-5 py-4 border-b flex items-center justify-between gap-4 transition-colors hover:bg-opacity-70 ${headerBg}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${uncovered > 0 ? 'bg-red-500' : unsigned > 0 ? 'bg-amber-500' : 'bg-green-500'}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{job.jobNumber}</span>
              <span className="text-xs text-gray-500">{(job as any).name ?? (job as any).description ?? ''}</span>
              <span className="text-xs text-gray-400">{activities.length} activities</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Mini status pills */}
          <div className="hidden sm:flex items-center gap-1.5">
            {covered > 0    && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{covered} covered</span>}
            {unsigned > 0   && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{unsigned} unsigned</span>}
            {uncovered > 0  && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">{uncovered} no SWMS</span>}
          </div>

          {/* Coverage % */}
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${coveredPct}%` }} />
            </div>
            <span className={`text-xs font-semibold tabular-nums w-8 ${uncovered > 0 ? 'text-red-700' : 'text-green-700'}`}>{coveredPct}%</span>
          </div>

          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* Activity table */}
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-36">Status</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Activity</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-40">Reg 291 categories</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-32">Source</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-24">Planned</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-40">Linked SWMS</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-36">Action</th>
              </tr>
            </thead>
            <tbody>
              {activities.map(a => (
                <ActivityRow key={a.id} activity={a} onAssign={onAssign} onMarkNotRequired={onMarkNotRequired} isWHSO={isWHSO} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WHSCoverageMapping() {
  const { isTier } = useAuthStore()
  const isWHSO  = isTier('C_WHSO')
  const canAct  = isWHSO || isTier('A_DEVELOPER')

  const [activities, setActivities] = useState<JobActivity[]>(MOCK_ACTIVITIES)
  const [jobFilter, setJobFilter]   = useState('ALL')
  const [statusFilter, setStatusFilter] = useState<CoverageStatus | 'ALL'>('ALL')
  const [assignTarget, setAssignTarget]   = useState<JobActivity | null>(null)
  const [notReqTarget, setNotReqTarget]   = useState<JobActivity | null>(null)
  // WHS Item 11: A04 Reg 291 classifier results
  const [agentClassifying, setAgentClassifying] = useState(false)
  const [agentResults, setAgentResults] = useState<import('../utils/mockDataProjectGaps').Reg291ClassificationResult[]>([])
  const [agentPanelOpen, setAgentPanelOpen] = useState(false)

  const handleA04Classify = () => {
    setAgentClassifying(true)
    setTimeout(() => {
      const input = activities.map(a => ({
        id: a.id,
        activityDescription: a.activityDescription,
        reg291Categories: a.reg291Categories,
      }))
      const results = runA04GapDetection(input)
      setAgentResults(results)
      setAgentPanelOpen(results.length > 0)
      setAgentClassifying(false)
      // Log A04 agent activity
      if (results.length > 0) {
        addAgentActivity({
          jobId: 'j1',   // broadest-scope job for platform-level agent log
          agentCode: 'A04', agentName: 'WHS Officer',
          agentWave: 'Wave 1',
          action: `Reg 291 gap analysis — ${results.length} categor${results.length !== 1 ? 'ies' : 'y'} detected across ${activities.length} activities`,
          detail: `Auto-classified ${activities.length} job activities against Schedule 2 WHS Regulation 2011 (Qld). Detected ${results.length} activities with missing or additional Reg 291 categories. Review and apply updates in Coverage Mapping module.`,
          confidence: 'HIGH',
          autonomyLevel: 1,
          timestamp: new Date().toISOString(),
          humanResponse: 'PENDING',
          linkedModule: 'WHS Coverage Mapping',
          linkedRecordId: undefined,
        })
      }
    }, 900)
  }

  const handleApplyClassification = (result: import('../utils/mockDataProjectGaps').Reg291ClassificationResult) => {
    setActivities(prev => prev.map(a => {
      if (a.id !== result.activityId) return a
      const merged = [...new Set([...a.reg291Categories, ...result.newCategories])]
      return { ...a, reg291Categories: merged }
    }))
    setAgentResults(prev => prev.filter(r => r.activityId !== result.activityId))
  }

  // ── Stats ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total       = activities.length
    const covered     = activities.filter(a => a.coverageStatus === 'covered').length
    const unsigned    = activities.filter(a => a.coverageStatus === 'swms_unsigned').length
    const uncovered   = activities.filter(a => a.coverageStatus === 'no_swms').length
    const notRequired = activities.filter(a => a.coverageStatus === 'not_required').length
    const relevant    = total - notRequired
    const pct         = relevant > 0 ? Math.round(((covered + unsigned) / relevant) * 100) : 100
    return { total, covered, unsigned, uncovered, notRequired, pct }
  }, [activities])

  // ── Filtered activities ──────────────────────────────────────
  const filtered = useMemo(() => {
    return activities.filter(a =>
      (jobFilter === 'ALL' || a.jobId === jobFilter) &&
      (statusFilter === 'ALL' || a.coverageStatus === statusFilter)
    )
  }, [activities, jobFilter, statusFilter])

  // ── Group by job ─────────────────────────────────────────────
  const byJob = useMemo(() => {
    const groups: Record<string, JobActivity[]> = {}
    filtered.forEach(a => {
      if (!groups[a.jobId]) groups[a.jobId] = []
      groups[a.jobId].push(a)
    })
    return groups
  }, [filtered])

  const activeJobIds = useMemo(() =>
    [...new Set(activities.map(a => a.jobId))],
    [activities]
  )

  // ── Handlers ─────────────────────────────────────────────────
  const handleAssign = (activityId: string, swmsId: string) => {
    const swms = MOCK_SWMS.find(s => s.id === swmsId)
    if (!swms) return
    setActivities(prev => prev.map(a =>
      a.id === activityId ? {
        ...a,
        coverageStatus:  'swms_unsigned',
        linkedSwmsId:    swms.id,
        linkedSwmsNumber: swms.swmsNumber,
        linkedSwmsTitle:  swms.title,
      } : a
    ))
    // API call stub: POST /api/job-activities/:activityId/coverage
    console.log('[WHSCoverage] Assigning SWMS', swmsId, 'to activity', activityId)
  }

  const handleNotRequired = (activityId: string, _reason: string) => {
    setActivities(prev => prev.map(a =>
      a.id === activityId ? { ...a, coverageStatus: 'not_required', linkedSwmsId: null, linkedSwmsNumber: null, linkedSwmsTitle: null } : a
    ))
  }

  const uncoveredOnActiveJobs = activities.filter(a => a.coverageStatus === 'no_swms')

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-blue-600" />
            SWMS Coverage Map
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Planned high-risk activities per active job · SWMS coverage status · Reg 291 compliance
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleA04Classify}
            disabled={agentClassifying}
            className="text-sm flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-60 transition-colors">
            {agentClassifying
              ? <><RefreshCw className="w-4 h-4 animate-spin" />A04 classifying…</>
              : <><Bot className="w-4 h-4" />A04 Classify</>}
          </button>
          <button className="btn text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" />Add activity
          </button>
          <button className="btn text-sm flex items-center gap-1.5">
            <Download className="w-4 h-4" />Export gap report
          </button>
          <button className="btn text-sm flex items-center gap-1.5">
            <RefreshCw className="w-4 h-4" />Refresh from register
          </button>
        </div>
      </div>

      {/* Critical alert */}
      {uncoveredOnActiveJobs.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-5 py-4 flex items-start gap-3">
          <ShieldX className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-red-800">
              {uncoveredOnActiveJobs.length} planned activit{uncoveredOnActiveJobs.length !== 1 ? 'ies' : 'y'} across {[...new Set(uncoveredOnActiveJobs.map(a => a.jobId))].length} job{[...new Set(uncoveredOnActiveJobs.map(a => a.jobId))].length !== 1 ? 's' : ''} have no approved SWMS.
            </div>
            <p className="text-xs text-red-700 mt-1">
              Under WHS Regulation 2011 (Qld) Schedule 2, high-risk construction work must not commence without an approved SWMS. Assign or create an approved SWMS before the planned start date.
            </p>
          </div>
          <button onClick={() => setStatusFilter('no_swms')} className="text-xs text-red-700 bg-red-100 hover:bg-red-200 border border-red-300 px-2.5 py-1.5 rounded-lg font-medium whitespace-nowrap flex-shrink-0">
            Show gaps only
          </button>
        </div>
      )}

      {/* A04 Agent classification results */}
      {agentPanelOpen && agentResults.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-300 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-900">A04 WHS Officer — Reg 291 classification results</span>
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{agentResults.length} activit{agentResults.length !== 1 ? 'ies' : 'y'} with additional categories detected</span>
            </div>
            <button onClick={() => setAgentPanelOpen(false)} className="p-1 text-indigo-400 hover:text-indigo-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-indigo-700">
            The A04 agent scanned all {activities.length} activities against Schedule 2 of the WHS Regulation 2011 (Qld).
            The activities below have additional Reg 291 HRCW categories detected that are not yet in the register.
            Review and apply, or dismiss if not applicable.
          </p>
          <div className="space-y-2">
            {agentResults.slice(0, 5).map(r => (
              <div key={r.activityId} className="bg-white border border-indigo-200 rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 leading-snug">{r.description.slice(0, 80)}{r.description.length > 80 ? '…' : ''}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {r.newCategories.map(c => (
                        <span key={c} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded leading-tight flex items-center gap-0.5">
                          <Zap className="w-2.5 h-2.5" />{c.length > 40 ? c.slice(0, 40) + '…' : c}
                        </span>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">Confidence: {r.confidence} · {r.newCategories.length} new categor{r.newCategories.length !== 1 ? 'ies' : 'y'}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => handleApplyClassification(r)}
                      className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded-lg font-medium transition-colors flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />Apply
                    </button>
                    <button onClick={() => setAgentResults(prev => prev.filter(rr => rr.activityId !== r.activityId))}
                      className="text-xs border border-gray-300 text-gray-500 hover:bg-gray-50 px-2 py-1 rounded-lg transition-colors">
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {agentResults.length > 5 && (
              <p className="text-xs text-indigo-600 text-center">+{agentResults.length - 5} more activities with detected categories</p>
            )}
          </div>
        </div>
      )}
      {agentPanelOpen && agentResults.length === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <strong>A04 classification complete —</strong> All activities have up-to-date Reg 291 category assignments. No additional categories detected.
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: 'Activities covered',
            value: stats.covered,
            sub: 'SWMS approved & signed on',
            bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200',
          },
          {
            label: 'Crew sign-on pending',
            value: stats.unsigned,
            sub: 'SWMS assigned, crew not signed',
            bg: stats.unsigned > 0 ? 'bg-amber-50' : 'bg-gray-50',
            text: stats.unsigned > 0 ? 'text-amber-700' : 'text-gray-400',
            border: stats.unsigned > 0 ? 'border-amber-200' : 'border-gray-200',
          },
          {
            label: 'No SWMS — at risk',
            value: stats.uncovered,
            sub: 'Work must not commence',
            bg: stats.uncovered > 0 ? 'bg-red-50' : 'bg-gray-50',
            text: stats.uncovered > 0 ? 'text-red-700' : 'text-gray-400',
            border: stats.uncovered > 0 ? 'border-red-300' : 'border-gray-200',
          },
          {
            label: 'Coverage rate',
            value: `${stats.pct}%`,
            sub: `${stats.covered + stats.unsigned} of ${stats.total - stats.notRequired} HRCW activities`,
            bg: stats.pct >= 100 ? 'bg-green-50' : stats.pct >= 70 ? 'bg-amber-50' : 'bg-red-50',
            text: stats.pct >= 100 ? 'text-green-700' : stats.pct >= 70 ? 'text-amber-700' : 'text-red-700',
            border: stats.pct >= 100 ? 'border-green-200' : stats.pct >= 70 ? 'border-amber-200' : 'border-red-300',
          },
        ].map(s => (
          <div key={s.label} className={`border rounded-xl px-4 py-3 ${s.bg} ${s.border}`}>
            <div className={`text-2xl font-bold ${s.text}`}>{s.value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{s.label}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm w-auto">
          <option value="ALL">All active jobs</option>
          {activeJobIds.map(jid => {
            const j = MOCK_JOBS.find(jj => jj.id === jid)
            return j ? <option key={jid} value={jid}>{j.jobNumber}</option> : null
          })}
        </select>

        <div className="flex gap-1 flex-wrap">
          {([
            ['ALL',          'All activities'],
            ['no_swms',      'No SWMS'],
            ['swms_unsigned','Crew unsigned'],
            ['covered',      'Covered'],
            ['not_required', 'Not required'],
          ] as [string, string][]).map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v as any)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                statusFilter === v ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {l}{v !== 'ALL' && ` (${activities.filter(a => a.coverageStatus === v && (jobFilter === 'ALL' || a.jobId === jobFilter)).length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-500 bg-gray-50 rounded-xl px-4 py-2.5">
        <span className="font-medium text-gray-600">Legend:</span>
        {Object.entries(COVERAGE_CONFIG).map(([k, v]) => {
          const LIcon = v.Icon
          return (
            <span key={k} className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${v.bg} ${v.text}`}>
              <LIcon className="w-3 h-3" />{v.short}
            </span>
          )
        })}
      </div>

      {/* Per-job panels */}
      <div className="space-y-3">
        {Object.entries(byJob).length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl py-14 text-center text-gray-400">
            <Shield className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="font-medium">No activities match the current filters</p>
          </div>
        ) : (
          Object.entries(byJob).map(([jid, acts]) => (
            <JobCoveragePanel
              key={jid}
              jobId={jid}
              activities={acts}
              isWHSO={canAct}
              onAssign={setAssignTarget}
              onMarkNotRequired={setNotReqTarget}
            />
          ))
        )}
      </div>

      {/* Info footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 flex items-start gap-2">
        <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-600" />
        <div>
          <strong>How activities reach this register:</strong> Activities are automatically fed from IFC drawings (drawing status update), Work Instruction step types, and ITP hold points. They can also be added manually by the WHSO. The <em>Source</em> column shows the origin of each entry. Full automation of drawing → activity creation requires the Job Activity Register API to be wired into the Drawings module (Phase 3).
        </div>
      </div>

      {/* Modals */}
      {assignTarget && (
        <AssignSWMSModal
          activity={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssign={handleAssign}
        />
      )}
      {notReqTarget && (
        <MarkNotRequiredModal
          activity={notReqTarget}
          onClose={() => setNotReqTarget(null)}
          onConfirm={handleNotRequired}
        />
      )}
    </div>
  )
}
